/**
 * The message box, and the lock that replaces it.
 *
 * *** A MENTOR MUST NEVER SEE THE STUDENT'S UPSELL ***
 * When a plan lapses the server locks BOTH parties — `check_quota` takes the
 * viewer's id and answers for them. But `upsell_message` is admin-authored
 * copy aimed at a student ("buy a plan to keep chatting"), and showing it to
 * a mentor tells them to buy something they cannot buy, about an account
 * that is not theirs.
 *
 * So the lock is one rule and the WORDING is two.
 */

import { useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { ApiError, uploadChatAttachment } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import type { ChatQuota, UploadedAttachment } from '@/types/api'
import styles from './MessagesPage.module.css'

export function Composer({
  locked,
  isMentor,
  quota,
  threadId,
  sending,
  onSend,
}: {
  locked: boolean
  isMentor: boolean
  quota: ChatQuota | null
  threadId: string
  sending: boolean
  onSend: (body: string, attachmentId: string | null) => Promise<void>
}) {
  const toast = useToast()
  const [text, setText] = useState('')
  const [pending, setPending] = useState<UploadedAttachment | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  if (locked) {
    return (
      <div className={styles.locked}>
        {isMentor ? (
          /*
            A mentor's side of the same lock. States the fact and stops —
            nothing here is theirs to fix, and the student has their own
            prompt on their own screen.
          */
          <p className={styles.lockedText}>
            This conversation is closed until the student&rsquo;s plan is
            active again. You can still read it.
          </p>
        ) : (
          <>
            <p className={styles.lockedText}>
              {/*
                The admin's own copy. A hardcoded replacement would drift
                from whatever the institute configured.
              */}
              {quota?.upsell_message ??
                'Your plan does not cover messaging right now.'}
            </p>
            <Button
              variant="primary"
              onClick={() => {
                window.location.assign('/plan')
              }}
            >
              See plans
            </Button>
          </>
        )}
      </div>
    )
  }

  const pickFile = async (file: File) => {
    setUploading(true)
    try {
      /*
        Two steps by design: upload first, then send a message referencing
        the id. A failed upload therefore leaves no broken message behind.
      */
      setPending(await uploadChatAttachment(threadId, file))
    } catch (caught) {
      /*
        The server names the reason — too large, wrong type, a PDF whose
        bytes are not a PDF. Each needs a different action.
      */
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not upload that file.',
      )
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const submit = async () => {
    const body = text.trim()
    if (!body && !pending) return
    try {
      await onSend(body, pending?.attachment_id ?? null)
      setText('')
      setPending(null)
    } catch (caught) {
      /*
        403 here is the quota, enforced server-side regardless of what this
        component believed. Its message is the admin's upsell.
      */
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not send that message.',
      )
    }
  }

  const canSend = (text.trim().length > 0 || pending !== null) && !sending

  return (
    <div className={styles.composer}>
      {/* What is attached but not yet sent. Removable — an accidental
          upload should not have to be sent to get rid of. */}
      {pending && (
        <div className={styles.pending}>
          <span className={styles.pendingName}>{pending.file_name}</span>
          <button
            type="button"
            className={styles.pendingX}
            onClick={() => setPending(null)}
            aria-label="Remove attachment"
          >
            ×
          </button>
        </div>
      )}

      <div className={styles.composerRow}>
        <input
          ref={fileRef}
          type="file"
          className={styles.hiddenFile}
          accept="image/*,application/pdf"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void pickFile(file)
          }}
        />
        <Button
          variant="ghost"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || pending !== null}
          aria-label="Attach a file"
        >
          {uploading ? '…' : '+'}
        </Button>

        <textarea
          className={styles.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a message"
          rows={1}
          maxLength={4000}
          onKeyDown={(e) => {
            /*
              Enter sends, Shift+Enter breaks the line — what every
              messaging app does, and what fingers expect.
            */
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void submit()
            }
          }}
        />

        <Button
          variant="primary"
          onClick={submit}
          disabled={!canSend}
          loading={sending}
        >
          Send
        </Button>
      </div>

      {/*
        How many free messages are left, when the institute caps them.
        Only shown to the person it constrains.
      */}
      {!isMentor &&
        quota?.messages_remaining !== null &&
        quota?.messages_remaining !== undefined && (
          <p className={styles.quotaNote}>
            {quota.messages_remaining === 0
              ? 'This is your last free message.'
              : `${quota.messages_remaining} free messages left.`}
          </p>
        )}
    </div>
  )
}
